<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  automationId: {
    type: [Number, String],
    default: null,
  },
  publishedVersionId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const SIMULATABLE_STATUSES = [
  'draft',
  'in_review',
  'approved',
  'published',
  'paused',
];

const QUERY_KEYS = Object.freeze({
  sampleEnabled: 'automation_sim_sample',
  sampleLimit: 'automation_sim_limit',
});

const versions = ref([]);
const simulation = ref(null);
const comparison = ref(null);
const comparisonError = ref('');
const isLoadingVersions = ref(false);
const isLoadingComparison = ref(false);
const isRunning = ref(false);
const feedbackState = ref('idle');
const feedbackDetail = ref('');
const sampleEnabled = ref(true);
const sampleLimit = ref(10);
let activeRequestId = 0;
let lastWrittenQuerySignature = '';

const queryValue = value => (Array.isArray(value) ? value[0] : value);

const routeQuery = () => route?.query || {};

const querySignature = query =>
  JSON.stringify(
    Object.keys(query || {})
      .sort()
      .map(key => [key, query[key]])
  );

const setQueryValue = (query, key, value, defaultValue = '') => {
  const normalized = value === null || value === undefined ? '' : String(value);
  if (normalized === '' || normalized === String(defaultValue)) {
    delete query[key];
  } else {
    query[key] = normalized;
  }
};

const booleanQueryValue = (value, fallback) => {
  const normalized = queryValue(value);
  if (['true', '1'].includes(normalized)) return true;
  if (['false', '0'].includes(normalized)) return false;
  return fallback;
};

const limitQueryValue = (value, fallback = 10) => {
  const parsed = Number.parseInt(queryValue(value), 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(Math.max(parsed, 1), 50);
};

const applySimulationQuery = (query = routeQuery()) => {
  sampleEnabled.value = booleanQueryValue(
    query[QUERY_KEYS.sampleEnabled],
    true
  );
  sampleLimit.value = limitQueryValue(query[QUERY_KEYS.sampleLimit], 10);
};

const replaceSimulationQuery = () => {
  const nextQuery = { ...routeQuery() };
  setQueryValue(nextQuery, QUERY_KEYS.sampleEnabled, sampleEnabled.value, true);
  setQueryValue(
    nextQuery,
    QUERY_KEYS.sampleLimit,
    limitQueryValue(sampleLimit.value, 10),
    10
  );

  const nextSignature = querySignature(nextQuery);
  if (nextSignature === querySignature(routeQuery())) return;

  lastWrittenQuerySignature = nextSignature;
  const result = router.replace({ query: nextQuery });
  result?.catch?.(() => {});
};

const newestFirst = records =>
  [...records].sort((first, second) => {
    const firstNumber = Number(first.version_number || first.id || 0);
    const secondNumber = Number(second.version_number || second.id || 0);
    return secondNumber - firstNumber;
  });

const candidateVersion = computed(() =>
  newestFirst(versions.value).find(version =>
    SIMULATABLE_STATUSES.includes(version.status)
  )
);

const normalizedSampleLimit = computed(() => {
  const parsed = Number(sampleLimit.value);
  if (!Number.isFinite(parsed)) return 10;
  return Math.min(Math.max(Math.trunc(parsed), 1), 50);
});

applySimulationQuery(routeQuery());

const canRunSimulation = computed(
  () =>
    Boolean(props.automationId && candidateVersion.value) &&
    !isLoadingVersions.value &&
    !isRunning.value
);

const impactEstimate = computed(() => simulation.value?.impact_estimate || {});
const actionPreviews = computed(() => simulation.value?.action_previews || []);
const conditionResults = computed(
  () => simulation.value?.condition_results || []
);
const sample = computed(() => simulation.value?.sample || {});
const sampleConversations = computed(() => sample.value?.conversations || []);
const candidateFailurePolicy = computed(
  () => candidateVersion.value?.failure_policy || {}
);
const configuredApprovalActions = computed(() =>
  Array.isArray(candidateFailurePolicy.value.approval_actions)
    ? candidateFailurePolicy.value.approval_actions.map(String)
    : []
);
const candidateActions = computed(() => {
  if (actionPreviews.value.length) return actionPreviews.value;
  return (candidateVersion.value?.actions || []).map((action, index) => ({
    index,
    action_name: action.action_name,
    would_execute: null,
  }));
});

const candidateGateStatus = computed(() => {
  const version = candidateVersion.value;
  if (!version) return 'unavailable';
  if (version.approved_by_id && version.approved_at) return 'approved';
  if (['published', 'scheduled', 'paused'].includes(version.status)) {
    return 'approved';
  }
  if (version.status === 'in_review') return 'pending';
  return version.status || 'unavailable';
});

function formatDate(value) {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatStatus(value) {
  if (value === null || value === undefined || value === '') {
    return t('AUTOMATION.APPROVAL_RISK_MATRIX.EMPTY');
  }
  return value.toString().replaceAll('_', ' ');
}

const candidateVersionRows = computed(() => {
  const version = candidateVersion.value || {};
  return [
    ['version_number', version.version_number],
    ['version_id', version.id],
    ['status', version.status],
    ['lock_version', version.lock_version],
    ['source_version_id', version.source_version_id],
    ['created_by_id', version.created_by_id],
    ['submitted_by_id', version.submitted_by_id],
    ['approved_by_id', version.approved_by_id],
    ['published_by_id', version.published_by_id],
    ['submitted_at', formatDate(version.submitted_at)],
    ['approved_at', formatDate(version.approved_at)],
    ['published_at', formatDate(version.published_at)],
  ].filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );
});

const actionRequiresApproval = action => {
  if (action.approval?.requires_approval !== undefined) {
    return action.approval.requires_approval;
  }
  if (action.requires_approval !== undefined) return action.requires_approval;
  if (!candidateFailurePolicy.value.approval_required) return false;
  if (!configuredApprovalActions.value.length) return true;
  return configuredApprovalActions.value.includes(action.action_name);
};

const actionRiskLevel = action =>
  action.approval?.risk_level ||
  action.risk_level ||
  candidateFailurePolicy.value.risk_level ||
  'none';

const actionGateRows = computed(() =>
  candidateActions.value.map(action => ({
    key: `${action.index}:${action.action_name}`,
    action_name: action.action_name,
    would_execute: action.would_execute,
    requires_approval: actionRequiresApproval(action),
    risk_level: actionRiskLevel(action),
    gate_status: actionRequiresApproval(action)
      ? candidateGateStatus.value
      : 'not_required',
  }))
);

const comparisonExplanation = computed(
  () => comparison.value?.explanation || {}
);
const comparisonDeltaRows = computed(() => [
  ['diff_state', comparisonExplanation.value.diff_state],
  ['simulation_state', comparisonExplanation.value.simulation_state],
  ['operational_summary', comparisonExplanation.value.operational_summary],
  ['total_changes', comparisonExplanation.value.total_changes],
]);
const comparisonContractRows = computed(() => {
  const contract = comparison.value?.read_only_contract || {};
  return [
    ['writes', contract.writes],
    ['effect_services_called', contract.effect_services_called],
    ['action_executor_called', contract.action_executor_called],
    ['provider_calls', contract.provider_calls],
    ['raw_action_params_exposed', contract.raw_action_params_exposed],
  ].filter(([, value]) => value !== null && value !== undefined);
});

const comparisonSimulationSummary = key => {
  const summary = comparison.value?.simulations?.[key] || {};
  const impact = summary.impact_estimate || {};
  return [
    ['status', summary.status],
    ['version_id', summary.automation_rule_version_id],
    ['event_name', summary.event_name],
    ['conditions_matched', summary.conditions_matched],
    [
      'estimated_matching_conversations',
      impact.estimated_matching_conversations,
    ],
    ['estimated_action_executions', impact.estimated_action_executions],
    ['estimated_cost_units', impact.estimated_cost_units],
  ].filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );
};

const summaryCards = computed(() => [
  {
    key: 'fanout',
    label: t('AUTOMATION.SIMULATION.SUMMARY.FANOUT'),
    value: impactEstimate.value.estimated_matching_conversations ?? 0,
  },
  {
    key: 'executions',
    label: t('AUTOMATION.SIMULATION.SUMMARY.EXECUTIONS'),
    value: impactEstimate.value.estimated_action_executions ?? 0,
  },
  {
    key: 'cost',
    label: t('AUTOMATION.SIMULATION.SUMMARY.COST'),
    value: impactEstimate.value.estimated_cost_units ?? 0,
  },
  {
    key: 'sample',
    label: t('AUTOMATION.SIMULATION.SUMMARY.SAMPLE'),
    value: t('AUTOMATION.SIMULATION.SUMMARY.SAMPLE_VALUE', {
      matched: sample.value.matched_count ?? 0,
      evaluated: sample.value.evaluated_count ?? 0,
    }),
  },
]);

const feedbackMessage = computed(() => {
  if (isLoadingVersions.value) return t('AUTOMATION.SIMULATION.LOADING');
  if (isRunning.value) return t('AUTOMATION.SIMULATION.RUNNING');
  if (feedbackState.value === 'load_error') {
    return t('AUTOMATION.SIMULATION.LOAD_ERROR');
  }
  if (feedbackState.value === 'permission') {
    return t('AUTOMATION.SIMULATION.PERMISSION');
  }
  if (feedbackState.value === 'validation') {
    return t('AUTOMATION.SIMULATION.VALIDATION');
  }
  if (feedbackState.value === 'error') return t('AUTOMATION.SIMULATION.ERROR');
  if (feedbackState.value === 'success') {
    return t('AUTOMATION.SIMULATION.SUCCESS');
  }
  if (!candidateVersion.value) return t('AUTOMATION.SIMULATION.EMPTY');
  return t('AUTOMATION.SIMULATION.READY');
});

const feedbackToneClass = computed(() => {
  if (feedbackState.value === 'success') {
    return 'bg-n-teal-3 text-n-teal-12';
  }
  if (
    ['permission', 'validation', 'load_error', 'error'].includes(
      feedbackState.value
    )
  ) {
    return 'bg-n-ruby-2 text-n-ruby-11';
  }
  return 'bg-n-slate-2 text-n-slate-11';
});

const feedbackRole = computed(() =>
  ['permission', 'validation', 'load_error', 'error'].includes(
    feedbackState.value
  )
    ? 'alert'
    : 'status'
);

function versionLabel(version) {
  if (!version) return t('AUTOMATION.SIMULATION.VERSION_EMPTY');
  if (version.version_number) {
    return t('AUTOMATION.SIMULATION.VERSION_LABEL', {
      version: version.version_number,
    });
  }
  return t('AUTOMATION.SIMULATION.VERSION_ID', { id: version.id });
}

function statusLabel(status) {
  switch (status) {
    case 'matched':
      return t('AUTOMATION.SIMULATION.STATUS.MATCHED');
    case 'not_matched':
      return t('AUTOMATION.SIMULATION.STATUS.NOT_MATCHED');
    case 'suppressed':
      return t('AUTOMATION.SIMULATION.STATUS.SUPPRESSED');
    case 'sampled':
      return t('AUTOMATION.SIMULATION.STATUS.SAMPLED');
    case 'invalid':
      return t('AUTOMATION.SIMULATION.STATUS.INVALID');
    default:
      return t('AUTOMATION.SIMULATION.STATUS.UNKNOWN');
  }
}

function executionLabel(preview) {
  return preview.would_execute
    ? t('AUTOMATION.SIMULATION.ACTION.WOULD_EXECUTE')
    : t('AUTOMATION.SIMULATION.ACTION.SUPPRESSED');
}

function suppressionLabel(reason) {
  switch (reason) {
    case 'sample_only_no_selected_conversation':
      return t('AUTOMATION.SIMULATION.SUPPRESSION.SAMPLE_ONLY');
    case 'conditions_not_matched':
      return t('AUTOMATION.SIMULATION.SUPPRESSION.CONDITIONS_NOT_MATCHED');
    case 'conversation_required':
    case 'conversation_required_for_conditions':
      return t('AUTOMATION.SIMULATION.SUPPRESSION.CONVERSATION_REQUIRED');
    case 'contact_required':
      return t('AUTOMATION.SIMULATION.SUPPRESSION.CONTACT_REQUIRED');
    default:
      return reason || t('AUTOMATION.SIMULATION.SUPPRESSION.NONE');
  }
}

function conditionStatusLabel(status) {
  switch (status) {
    case 'matched_overall':
      return t('AUTOMATION.SIMULATION.CONDITION.MATCHED_OVERALL');
    case 'not_matched_overall':
      return t('AUTOMATION.SIMULATION.CONDITION.NOT_MATCHED_OVERALL');
    case 'not_evaluated':
      return t('AUTOMATION.SIMULATION.CONDITION.NOT_EVALUATED');
    case 'sampled_only':
      return t('AUTOMATION.SIMULATION.CONDITION.SAMPLED_ONLY');
    default:
      return status || t('AUTOMATION.SIMULATION.CONDITION.UNKNOWN');
  }
}

function sampleStatusLabel(row) {
  return row.matched
    ? t('AUTOMATION.SIMULATION.SAMPLE_ROW.MATCHED')
    : t('AUTOMATION.SIMULATION.SAMPLE_ROW.NOT_MATCHED');
}

function mapErrorState(error) {
  const status = error?.response?.status;
  feedbackDetail.value = error?.response?.data?.error || '';
  if (status === 403) return 'permission';
  if (status === 422) return 'validation';
  return 'error';
}

async function loadComparison(simulationRequest) {
  comparison.value = null;
  comparisonError.value = '';
  if (!props.automationId || !candidateVersion.value) return;

  isLoadingComparison.value = true;
  try {
    comparison.value = await store.dispatch(
      'automations/candidatePublishedComparison',
      {
        id: props.automationId,
        versionId: candidateVersion.value.id,
        comparison: simulationRequest,
      }
    );
  } catch (error) {
    comparisonError.value =
      error?.response?.data?.error || error?.message || '';
  } finally {
    isLoadingComparison.value = false;
  }
}

async function loadVersions({ preserveResult = false } = {}) {
  const automationId = props.automationId;
  const requestId = activeRequestId + 1;
  activeRequestId = requestId;
  versions.value = [];
  feedbackDetail.value = '';
  if (!preserveResult) {
    simulation.value = null;
    comparison.value = null;
    comparisonError.value = '';
    feedbackState.value = 'idle';
  }

  if (!automationId) return;

  isLoadingVersions.value = true;

  try {
    const payload = await store.dispatch('automations/versions', automationId);
    if (requestId === activeRequestId) {
      versions.value = payload?.versions || [];
    }
  } catch (error) {
    if (requestId === activeRequestId) {
      feedbackState.value =
        error?.response?.status === 403 ? 'permission' : 'load_error';
      feedbackDetail.value = error?.response?.data?.error || '';
    }
  } finally {
    if (requestId === activeRequestId) {
      isLoadingVersions.value = false;
    }
  }
}

async function runSimulation() {
  if (!canRunSimulation.value) return;

  isRunning.value = true;
  simulation.value = null;
  comparison.value = null;
  comparisonError.value = '';
  feedbackState.value = 'idle';
  feedbackDetail.value = '';
  replaceSimulationQuery();

  try {
    const simulationRequest = {
      sample: {
        enabled: sampleEnabled.value,
        limit: normalizedSampleLimit.value,
      },
    };
    const payload = await store.dispatch('automations/simulate', {
      id: props.automationId,
      versionId: candidateVersion.value.id,
      simulation: simulationRequest,
    });
    simulation.value = payload;
    feedbackState.value = 'success';
    await loadComparison(simulationRequest);
  } catch (error) {
    feedbackState.value = mapErrorState(error);
  } finally {
    isRunning.value = false;
  }
}

watch(() => [props.automationId, props.publishedVersionId], loadVersions, {
  immediate: true,
});

watch(
  () => route?.query,
  query => {
    const signature = querySignature(query || {});
    if (signature === lastWrittenQuerySignature) {
      lastWrittenQuerySignature = '';
      return;
    }
    applySimulationQuery(query || {});
  },
  { deep: true }
);
</script>

<template>
  <section
    class="mb-5 rounded-lg border border-n-weak bg-n-background p-3"
    data-test="automation-simulation-panel"
  >
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <h3 class="mb-1 text-sm font-medium text-n-slate-12">
          {{ $t('AUTOMATION.SIMULATION.TITLE') }}
        </h3>
        <p class="m-0 text-xs text-n-slate-11">
          {{ $t('AUTOMATION.SIMULATION.SUBTITLE') }}
        </p>
      </div>
      <Button
        faded
        slate
        sm
        icon="i-lucide-refresh-cw"
        :label="$t('AUTOMATION.SIMULATION.REFRESH')"
        :is-loading="isLoadingVersions"
        data-test="automation-simulation-refresh"
        @click="loadVersions"
      />
    </div>

    <div class="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
      <div class="rounded-lg border border-n-weak px-3 py-2">
        <div class="text-xs font-medium text-n-slate-11">
          {{ $t('AUTOMATION.SIMULATION.CANDIDATE') }}
        </div>
        <div
          class="mt-1 text-sm font-medium text-n-slate-12"
          data-test="automation-simulation-candidate"
        >
          {{ versionLabel(candidateVersion) }}
        </div>
        <div class="mt-1 text-xs text-n-slate-11">
          {{
            candidateVersion
              ? $t('AUTOMATION.SIMULATION.CANDIDATE_STATUS', {
                  status: candidateVersion.status,
                })
              : $t('AUTOMATION.SIMULATION.NO_CANDIDATE')
          }}
        </div>
      </div>

      <div
        class="grid min-w-48 gap-2 rounded-lg border border-n-weak px-3 py-2"
      >
        <label
          class="m-0 flex min-h-8 items-center gap-2 text-xs text-n-slate-11"
        >
          <input
            v-model="sampleEnabled"
            type="checkbox"
            class="m-0"
            data-test="automation-simulation-sample-toggle"
            @change="replaceSimulationQuery"
          />
          {{ $t('AUTOMATION.SIMULATION.SAMPLE_TOGGLE') }}
        </label>
        <label class="m-0 grid gap-1 text-xs text-n-slate-11">
          {{ $t('AUTOMATION.SIMULATION.SAMPLE_LIMIT') }}
          <input
            v-model.number="sampleLimit"
            type="number"
            min="1"
            max="50"
            class="m-0 h-8 rounded-md border border-n-weak bg-n-alpha-2 px-2 text-sm text-n-slate-12"
            data-test="automation-simulation-sample-limit"
            @change="replaceSimulationQuery"
          />
        </label>
      </div>
    </div>

    <div
      v-if="candidateVersion"
      class="mt-3 grid gap-3 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]"
      data-test="automation-simulation-governance"
    >
      <div class="rounded-lg border border-n-weak px-3 py-2">
        <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('AUTOMATION.SIMULATION.CANDIDATE') }}
          </span>
          <span
            class="rounded-md bg-n-slate-3 px-2 py-1 text-xs text-n-slate-12"
          >
            {{ formatStatus(candidateGateStatus) }}
          </span>
        </div>
        <dl class="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-3 gap-y-1">
          <template
            v-for="[label, value] in candidateVersionRows"
            :key="`candidate:${label}`"
          >
            <dt class="text-xs text-n-slate-11">
              {{ formatStatus(label) }}
            </dt>
            <dd class="min-w-0 break-all text-xs text-n-slate-12">
              {{ value }}
            </dd>
          </template>
        </dl>
      </div>

      <div class="rounded-lg border border-n-weak px-3 py-2">
        <div class="mb-2 text-xs font-medium text-n-slate-11">
          {{ $t('AUTOMATION.APPROVAL_RISK_MATRIX.TABLE.TITLE') }}
        </div>
        <div class="grid gap-2" data-test="automation-simulation-gates">
          <div
            v-for="row in actionGateRows"
            :key="row.key"
            class="grid gap-1 rounded-md border border-n-weak px-3 py-2 text-xs text-n-slate-11 sm:grid-cols-[minmax(0,1fr)_7rem_7rem_7rem]"
          >
            <span class="truncate font-medium text-n-slate-12">
              {{ row.action_name }}
            </span>
            <span>{{ formatStatus(row.risk_level) }}</span>
            <span>{{ formatStatus(row.gate_status) }}</span>
            <span>
              {{
                row.requires_approval
                  ? $t('AUTOMATION.APPROVAL_RISK_MATRIX.YES')
                  : $t('AUTOMATION.APPROVAL_RISK_MATRIX.NO')
              }}
            </span>
          </div>
          <div
            v-if="!actionGateRows.length"
            class="rounded-md bg-n-slate-2 px-3 py-2 text-xs text-n-slate-11"
          >
            {{ $t('AUTOMATION.APPROVAL_RISK_MATRIX.EMPTY') }}
          </div>
        </div>
      </div>
    </div>

    <div
      class="mt-3 rounded-md px-3 py-2 text-sm"
      :class="feedbackToneClass"
      :role="feedbackRole"
      data-test="automation-simulation-feedback"
    >
      {{ feedbackMessage }}
      <span v-if="feedbackDetail" class="block pt-1 text-xs">
        {{ feedbackDetail }}
      </span>
    </div>

    <div class="mt-3 flex justify-end">
      <Button
        solid
        blue
        sm
        icon="i-lucide-play"
        :label="
          isRunning
            ? $t('AUTOMATION.SIMULATION.RUNNING_BUTTON')
            : $t('AUTOMATION.SIMULATION.RUN')
        "
        :disabled="!canRunSimulation"
        :is-loading="isRunning"
        data-test="automation-simulation-run"
        @click="runSimulation"
      />
    </div>

    <div v-if="simulation" class="mt-3 grid gap-3">
      <div class="flex flex-wrap items-center gap-2 text-xs text-n-slate-11">
        <span
          class="rounded-md bg-n-slate-3 px-2 py-1 font-medium text-n-slate-12"
          data-test="automation-simulation-status"
        >
          {{ statusLabel(simulation.status) }}
        </span>
        <span data-test="automation-simulation-event">
          {{ simulation.event_name }}
        </span>
      </div>

      <div class="grid gap-2 sm:grid-cols-4">
        <div
          v-for="item in summaryCards"
          :key="item.key"
          class="rounded-lg border border-n-weak px-3 py-2"
          :data-test="`automation-simulation-summary-${item.key}`"
        >
          <div class="text-xs text-n-slate-11">
            {{ item.label }}
          </div>
          <div class="text-base font-medium text-n-slate-12">
            {{ item.value }}
          </div>
        </div>
      </div>

      <div
        v-if="simulation.suppression_reasons?.length"
        class="rounded-md bg-n-amber-3 px-3 py-2 text-xs text-n-amber-12"
        data-test="automation-simulation-suppressions"
      >
        <span
          v-for="reason in simulation.suppression_reasons"
          :key="reason"
          class="mr-2"
        >
          {{ suppressionLabel(reason) }}
        </span>
      </div>

      <div
        class="grid gap-3 rounded-lg border border-n-weak px-3 py-2"
        data-test="automation-simulation-comparison"
      >
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('AUTOMATION.COMPARISON.TITLE') }}
          </span>
          <span
            v-if="isLoadingComparison"
            class="rounded-md bg-n-slate-3 px-2 py-1 text-xs text-n-slate-12"
          >
            {{ $t('AUTOMATION.SIMULATION.LOADING') }}
          </span>
        </div>

        <div
          v-if="comparisonError"
          class="rounded-md bg-n-ruby-2 px-3 py-2 text-xs text-n-ruby-11"
          role="alert"
          data-test="automation-simulation-comparison-error"
        >
          {{ $t('AUTOMATION.SIMULATION.LOAD_ERROR') }}
          <span v-if="comparisonError" class="block pt-1">
            {{ comparisonError }}
          </span>
        </div>

        <template v-if="comparison">
          <dl
            class="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1 sm:grid-cols-[9rem_minmax(0,1fr)_9rem_minmax(0,1fr)]"
          >
            <template
              v-for="[label, value] in comparisonDeltaRows"
              :key="`comparison:${label}`"
            >
              <dt class="text-xs text-n-slate-11">
                {{ formatStatus(label) }}
              </dt>
              <dd class="min-w-0 break-all text-xs text-n-slate-12">
                {{ formatStatus(value) }}
              </dd>
            </template>
          </dl>

          <div class="grid gap-3 lg:grid-cols-2">
            <div class="rounded-md border border-n-weak px-3 py-2">
              <div class="mb-2 text-xs font-medium text-n-slate-11">
                {{ formatStatus('candidate_simulation') }}
              </div>
              <dl class="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1">
                <template
                  v-for="[label, value] in comparisonSimulationSummary(
                    'candidate'
                  )"
                  :key="`candidate-sim:${label}`"
                >
                  <dt class="text-xs text-n-slate-11">
                    {{ formatStatus(label) }}
                  </dt>
                  <dd class="min-w-0 break-all text-xs text-n-slate-12">
                    {{ formatStatus(value) }}
                  </dd>
                </template>
              </dl>
            </div>

            <div class="rounded-md border border-n-weak px-3 py-2">
              <div class="mb-2 text-xs font-medium text-n-slate-11">
                {{ formatStatus('published_execution_baseline') }}
              </div>
              <dl class="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1">
                <template
                  v-for="[label, value] in comparisonSimulationSummary(
                    'published'
                  )"
                  :key="`published-sim:${label}`"
                >
                  <dt class="text-xs text-n-slate-11">
                    {{ formatStatus(label) }}
                  </dt>
                  <dd class="min-w-0 break-all text-xs text-n-slate-12">
                    {{ formatStatus(value) }}
                  </dd>
                </template>
              </dl>
              <div
                v-if="!comparisonSimulationSummary('published').length"
                class="rounded-md bg-n-slate-2 px-3 py-2 text-xs text-n-slate-11"
              >
                {{ $t('AUTOMATION.APPROVAL_RISK_MATRIX.EMPTY') }}
              </div>
            </div>
          </div>

          <dl
            class="grid grid-cols-[10rem_minmax(0,1fr)] gap-x-3 gap-y-1 rounded-md bg-n-slate-2 px-3 py-2"
            data-test="automation-simulation-readonly-contract"
          >
            <template
              v-for="[label, value] in comparisonContractRows"
              :key="`contract:${label}`"
            >
              <dt class="text-xs text-n-slate-11">
                {{ formatStatus(label) }}
              </dt>
              <dd class="min-w-0 break-all text-xs text-n-slate-12">
                {{ formatStatus(value) }}
              </dd>
            </template>
          </dl>
        </template>
      </div>

      <div class="grid gap-3 sm:grid-cols-2">
        <div>
          <h4 class="mb-2 text-xs font-medium text-n-slate-11">
            {{ $t('AUTOMATION.SIMULATION.CONDITION.TITLE') }}
          </h4>
          <ul class="m-0 grid list-none gap-2 p-0">
            <li
              v-for="condition in conditionResults"
              :key="condition.index"
              class="rounded-lg border border-n-weak p-3 text-xs text-n-slate-11"
              data-test="automation-simulation-condition"
            >
              <div class="font-medium text-n-slate-12">
                {{ condition.attribute_key || '-' }}
              </div>
              <div>
                {{ condition.filter_operator || '-' }}
              </div>
              <div>
                {{ conditionStatusLabel(condition.status) }}
              </div>
            </li>
          </ul>
          <p
            v-if="!conditionResults.length"
            class="m-0 rounded-md bg-n-slate-2 px-3 py-2 text-xs text-n-slate-11"
            data-test="automation-simulation-no-conditions"
          >
            {{ $t('AUTOMATION.SIMULATION.CONDITION.EMPTY') }}
          </p>
        </div>

        <div>
          <h4 class="mb-2 text-xs font-medium text-n-slate-11">
            {{ $t('AUTOMATION.SIMULATION.ACTION.TITLE') }}
          </h4>
          <ul class="m-0 grid list-none gap-2 p-0">
            <li
              v-for="preview in actionPreviews"
              :key="preview.index"
              class="rounded-lg border border-n-weak p-3 text-xs text-n-slate-11"
              data-test="automation-simulation-action"
            >
              <div class="flex flex-wrap items-center gap-2">
                <span class="font-medium text-n-slate-12">
                  {{ preview.action_name }}
                </span>
                <span
                  class="rounded-md px-2 py-1 font-medium"
                  :class="
                    preview.would_execute
                      ? 'bg-n-teal-3 text-n-teal-12'
                      : 'bg-n-slate-3 text-n-slate-11'
                  "
                >
                  {{ executionLabel(preview) }}
                </span>
              </div>
              <div class="mt-2">
                {{
                  $t('AUTOMATION.SIMULATION.ACTION.COST', {
                    count: preview.cost_units || 0,
                  })
                }}
              </div>
              <div v-if="preview.suppression_reason">
                {{ suppressionLabel(preview.suppression_reason) }}
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="sampleConversations.length">
        <h4 class="mb-2 text-xs font-medium text-n-slate-11">
          {{ $t('AUTOMATION.SIMULATION.SAMPLE_ROW.TITLE') }}
        </h4>
        <ul
          class="m-0 grid list-none gap-2 p-0"
          data-test="automation-simulation-sample-rows"
        >
          <li
            v-for="row in sampleConversations"
            :key="row.conversation_id"
            class="grid gap-1 rounded-lg border border-n-weak p-3 text-xs text-n-slate-11 sm:grid-cols-4"
          >
            <span class="font-medium text-n-slate-12">
              {{
                $t('AUTOMATION.SIMULATION.SAMPLE_ROW.CONVERSATION', {
                  id: row.display_id || row.conversation_id,
                })
              }}
            </span>
            <span>{{ row.status }}</span>
            <span>{{ sampleStatusLabel(row) }}</span>
            <span>
              {{
                $t('AUTOMATION.SIMULATION.SAMPLE_ROW.ACTIONS', {
                  count: row.would_execute_actions_count || 0,
                })
              }}
            </span>
          </li>
        </ul>
      </div>

      <ul
        v-if="impactEstimate.assumptions?.length"
        class="m-0 grid list-none gap-1 p-0 text-xs text-n-slate-11"
        data-test="automation-simulation-assumptions"
      >
        <li v-for="assumption in impactEstimate.assumptions" :key="assumption">
          {{ assumption }}
        </li>
      </ul>
    </div>
  </section>
</template>
